import localeProvider from "csi/page/localeProvider";
import { ICache } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, makeObservable, observable, runInAction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";

const LANGUAGE_SYNTAX_PERCENTAGE_TYPE_MATERIAL = "LanguageSyntax:PercentageTypeMaterial";
const LANGUAGE_SYNTAX_PERCENTAGE_MATERIAL_TYPE = "LanguageSyntax:PercentageMaterialType";
const LANGUAGE_SYNTAX_TYPE_MATERIAL_PERCENTAGE = "LanguageSyntax:TypeMaterialPercentage";
const LANGUAGE_SYNTAX_MATERIAL_TYPE_PERCENTAGE = "LanguageSyntax:MaterialTypePercentage";

export class CareAndCompositionSubtypeStore extends ReactionStore {
	/** cache for use inside the store */
	readonly cache: ICache;

	/** callback to save with save new */
	readonly _onSaveWithSaveNew: VoidFunction;

	/** callback to save new */
	readonly _onClickSaveNew: VoidFunction;

	/** determines if placement names should be included on the righthand side */
	@observable enforce100PercentPerPlacement: boolean = false;

	/** determines if zero percent entries are allowed */
	@observable allowZeroPercentEntries: boolean = false;

	/** determines if material composition edits are allowed */
	@observable allowMaterialCompositionEdits: boolean = false;

	/** determines if parent-child placements are enabled */
	@observable enableParentChildPlacements: boolean = false;

	@observable subtypeName: string;

	@observable.ref languageSyntax: ComponentItem;

	@observable subtypeBlankNameError: boolean = true;
	@observable subtypeExistsError: boolean = false;
	@observable languageSyntaxError: boolean = true;

	/** Retrieves an array of all available ComponentItems for placement, including the DUMMY item. */
	get languageSyntaxOptions(): ComponentItem[] {
		return [
			{ id: LANGUAGE_SYNTAX_PERCENTAGE_TYPE_MATERIAL, name: localeProvider.resource(LANGUAGE_SYNTAX_PERCENTAGE_TYPE_MATERIAL), value: 1 },
			{ id: LANGUAGE_SYNTAX_PERCENTAGE_MATERIAL_TYPE, name: localeProvider.resource(LANGUAGE_SYNTAX_PERCENTAGE_MATERIAL_TYPE), value: 2 },
			{ id: LANGUAGE_SYNTAX_TYPE_MATERIAL_PERCENTAGE, name: localeProvider.resource(LANGUAGE_SYNTAX_TYPE_MATERIAL_PERCENTAGE), value: 3 },
			{ id: LANGUAGE_SYNTAX_MATERIAL_TYPE_PERCENTAGE, name: localeProvider.resource(LANGUAGE_SYNTAX_MATERIAL_TYPE_PERCENTAGE), value: 4 },
		];
	}

	get disableSaveButton(): boolean {
		return this.subtypeBlankNameError || this.languageSyntaxError;
	};

	/** toggle enforce100PercentPerPlacement */
	@action setEnforce100PercentPerPlacement = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.enforce100PercentPerPlacement = value;
	};

	/** toggle allowZeroPercentEntries */
	@action setAllowZeroPercentEntries = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.allowZeroPercentEntries = value;
	};

	/** toggle allowMaterialCompositionEdits */
	@action setAllowMaterialCompositionEdits = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.allowMaterialCompositionEdits = value;
	};

	/** toggle enableParentChildPlacements */
	@action setEnableParentChildPlacements = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.enableParentChildPlacements = value;
	};

	/** set languageSyntax */
	@action setLanguageSyntax = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.languageSyntax = value;
		this.languageSyntaxError = value === undefined;
	};

	onClickSaveNew = (): void => {
		this._onClickSaveNew();
	};

	onSaveWithSaveNew = (): void => {
		this._onSaveWithSaveNew();
	};

	setName = (event: ChangeEvent<HTMLInputElement>): void => {
		runInAction(() => {
			this.subtypeName = event.target.value;
			this.subtypeBlankNameError = this.subtypeName === "";
			this.subtypeExistsError = false;
		});
	};

	resetStore = (): void => {
		this.subtypeName = "";
		this.languageSyntax = undefined;
		this.enforce100PercentPerPlacement = false;
		this.allowZeroPercentEntries = false;
		this.allowMaterialCompositionEdits = false;
		this.enableParentChildPlacements = false;
		this.subtypeBlankNameError = true;
		this.subtypeExistsError = false;
		this.languageSyntaxError = true;
	};

	constructor(cache: ICache, onSaveWithSaveNew: VoidFunction, onClickSaveNew: VoidFunction) {
		super();
		makeObservable(this);
		this.cache = cache;
		this._onSaveWithSaveNew = onSaveWithSaveNew;
		this._onClickSaveNew = onClickSaveNew;
	}
}

export const CareAndCompositionSubtypeStoreContext = createContext<CareAndCompositionSubtypeStore>(undefined);
export const useCareAndCompositionSubtypeStore = (): CareAndCompositionSubtypeStore => useContext(CareAndCompositionSubtypeStoreContext);
